import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { CheckCheck, CircleCheckBig, Lock, LockOpen, RotateCcw, Send, Trash2 } from 'lucide-react';
import { adminQueries, getErrorMessage } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { DOCUMENT_CHECK_RECEIPT_FOLDER, documentCheckSteps, formatDate, hasPermission } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmDialog,
  ErrorState,
  Label,
  PageHeader,
  Stepper,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@blimo/ui';
import { DetailSkeleton, PropertyLink } from '../../components/data.jsx';
import { SendDialog } from '../../components/send-dialog.jsx';
import { AuditorsCard, EventLog, OwnerMailsCard } from './panels.jsx';
import { FoldersTab } from './folders.jsx';
import { BookingListTab } from './booking-list.jsx';
import { CommentsTab } from './comments.jsx';
import { useInvalidateCheck } from './utils.js';

const TABS = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'ordner', label: 'Ordner' },
  { id: 'buchungsliste', label: 'Buchungsliste' },
  { id: 'kommentare', label: 'Kommentare' },
  { id: 'protokoll', label: 'Ereignisse' },
];

/** Belegprüfung › Detail (F-801…F-808). Every state shown comes from the API; actions are hidden without permission. */
export function DocumentCheckDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { checkId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const query = useQuery(adminQueries.documentCheck(api, checkId));
  const tab = TABS.some((t) => t.id === search.tab) ? search.tab : 'uebersicht';
  const setTab = (next) =>
    navigate({ to: '/belegpruefung/$checkId', params: { checkId }, search: { tab: next === 'uebersicht' ? undefined : next }, replace: true });

  const check = query.data?.data;
  const folders = (check?.folders ?? []).filter((f) => f.key !== DOCUMENT_CHECK_RECEIPT_FOLDER);
  const canViewDocuments = hasPermission(me, 'documents.view');
  // Documents of all folders (titles for comments, folder lists) – same query keys as the folder cards.
  const folderDocs = useQueries({
    queries: (check?.folders ?? []).map((f) => ({ ...adminQueries.folderDocuments(api, f.id), enabled: canViewDocuments })),
  });
  const documents = (check?.folders ?? []).flatMap((f, i) => (folderDocs[i]?.data ?? []).map((d) => ({ ...d, folder: f })));

  const breadcrumb = (
    <Link to="/belegpruefung" className="hover:underline">
      Belegprüfung
    </Link>
  );

  if (query.error) {
    return (
      <>
        <PageHeader title="Belegprüfung" breadcrumb={breadcrumb} />
        <ErrorState error={query.error} onRetry={() => query.refetch()} notFoundTitle="Belegprüfung nicht gefunden" />
      </>
    );
  }
  if (query.isPending) return <DetailSkeleton />;

  const { steps, current } = documentCheckSteps(check);
  const auditors = query.data.meta?.auditors ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`Belegprüfung ${check.fiscal_year}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <PropertyLink property={check.property} />
            {check.due_on && <span className="numeric">Abschluss bis {formatDate(check.due_on)}</span>}
            {check.has_booking_list && <Badge variant="info">Buchungsliste importiert</Badge>}
          </span>
        }
        actions={<WorkflowActions check={check} me={me} />}
      />
      <Card className="mb-4">
        <CardContent className="py-4">
          <Stepper label="Status der Belegprüfung" steps={steps} current={current} />
        </CardContent>
      </Card>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Bereiche der Belegprüfung">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="uebersicht">
          <div className="grid gap-3 lg:grid-cols-2">
            <AuditorsCard check={check} auditors={auditors} me={me} />
            <OwnerMailsCard check={check} me={me} />
          </div>
        </TabsContent>
        <TabsContent value="ordner">
          <FoldersTab check={check} folders={folders} me={me} />
        </TabsContent>
        <TabsContent value="buchungsliste">
          <BookingListTab check={check} me={me} />
        </TabsContent>
        <TabsContent value="kommentare">
          <CommentsTab check={check} documents={documents} me={me} />
        </TabsContent>
        <TabsContent value="protokoll">
          <EventLog events={check.events} />
        </TabsContent>
      </Tabs>
    </>
  );
}

/**
 * Freigeben / Zurücknehmen / Bereit / Abschließen / Löschen. Which action is offered follows the API flags;
 * the API decides (e.g. 409 `document-check-in-use` on delete).
 */
function WorkflowActions({ check, me }) {
  const api = useApi();
  const navigate = useNavigate();
  const invalidate = useInvalidateCheck();
  const [dialog, setDialog] = useState(/** @type {null|'release'|'withdraw'|'ready'|'complete'|'delete'} */ (null));
  const [notify, setNotify] = useState(true);
  const canRelease = hasPermission(me, 'document-checks.release');
  const canManage = hasPermission(me, 'document-checks.manage');
  const deletable = canManage && !check.released_to_auditors && !check.is_ready && !(check.confirmations?.length > 0);

  /** @param {() => Promise<unknown>} fn @param {string} success @param {string} failure */
  const run = (fn, success, failure) => async () => {
    try {
      await fn();
      await invalidate();
      toast.success(success);
    } catch (e) {
      toast.error(getErrorMessage(e, failure));
    }
  };

  async function remove() {
    try {
      await api.admin.documentChecks.remove(check.id);
      invalidate();
      toast.success('Belegprüfung gelöscht');
      navigate({ to: '/belegpruefung' });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Belegprüfung konnte nicht gelöscht werden.'));
    }
  }

  const close = (open) => !open && setDialog(null);

  return (
    <>
      {canRelease && !check.released_to_auditors && (
        <Button size="sm" onClick={() => setDialog('release')}>
          <LockOpen aria-hidden="true" /> Freigeben
        </Button>
      )}
      {canRelease && check.released_to_auditors && (
        <Button size="sm" variant="outline" onClick={() => setDialog('withdraw')}>
          <Lock aria-hidden="true" /> Freigabe zurücknehmen
        </Button>
      )}
      {canManage && check.released_to_auditors && !check.is_ready && (
        <Button size="sm" variant="outline" onClick={() => setDialog('ready')}>
          <CheckCheck aria-hidden="true" /> Als bereit markieren
        </Button>
      )}
      {canManage && (
        <Button size="sm" variant="outline" onClick={() => setDialog('complete')}>
          {check.is_completed ? <RotateCcw aria-hidden="true" /> : <CircleCheckBig aria-hidden="true" />}
          {check.is_completed ? 'Abschluss zurücknehmen' : 'Abschließen'}
        </Button>
      )}
      {deletable && (
        <Button size="sm" variant="outline" onClick={() => setDialog('delete')}>
          <Trash2 aria-hidden="true" /> Löschen
        </Button>
      )}

      <SendDialog
        open={dialog === 'release'}
        onOpenChange={close}
        title="Für Kassenprüfer freigeben?"
        description="Die Kassenprüfer sehen die Prüfung danach im Portal."
        confirmLabel="Freigeben"
        send={() => api.admin.documentChecks.release(check.id, { notify })}
        onSent={() => {
          invalidate();
          toast.success(notify ? 'Freigegeben – Kassenprüfer werden benachrichtigt' : 'Freigegeben');
        }}
      >
        <div className="flex items-center gap-2">
          <Checkbox id="release-notify" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <Label htmlFor="release-notify" className="font-normal">
            <Send className="mr-1 inline size-3.5" aria-hidden="true" />
            Kassenprüfer per E-Mail benachrichtigen
          </Label>
        </div>
      </SendDialog>
      <ConfirmDialog
        open={dialog === 'withdraw'}
        onOpenChange={close}
        title="Freigabe zurücknehmen?"
        description="Die Kassenprüfer sehen die Prüfung danach nicht mehr im Portal. Bestätigungen bleiben erhalten."
        confirmLabel="Zurücknehmen"
        onConfirm={run(() => api.admin.documentChecks.withdraw(check.id), 'Freigabe zurückgenommen', 'Die Freigabe konnte nicht zurückgenommen werden.')}
      />
      <ConfirmDialog
        open={dialog === 'ready'}
        onOpenChange={close}
        title="Als bereit markieren?"
        description="Die Prüfung gilt dann als bestätigt, auch wenn nicht alle Kassenprüfer im Portal bestätigt haben. Die Verwaltung wird informiert."
        confirmLabel="Als bereit markieren"
        onConfirm={run(() => api.admin.documentChecks.ready(check.id), 'Als bereit markiert', 'Der Status konnte nicht geändert werden.')}
      />
      <ConfirmDialog
        open={dialog === 'complete'}
        onOpenChange={close}
        title={check.is_completed ? 'Abschluss zurücknehmen?' : 'Belegprüfung abschließen?'}
        description={
          check.is_completed
            ? 'Die Eigentümer sehen die Prüfung danach nicht mehr im Portal.'
            : 'Mit dem Abschluss sehen die Eigentümer die Prüfung (bei „bereit“ mit allen Ordnern) im Portal.'
        }
        confirmLabel={check.is_completed ? 'Zurücknehmen' : 'Abschließen'}
        onConfirm={run(
          () => api.admin.documentChecks.update(check.id, { is_completed: !check.is_completed }),
          check.is_completed ? 'Abschluss zurückgenommen' : 'Belegprüfung abgeschlossen',
          'Der Status konnte nicht geändert werden.',
        )}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={close}
        title="Belegprüfung löschen?"
        description="Die Prüfung und ihre Ordner werden gelöscht; hochgeladene Dokumente bleiben erhalten."
        confirmLabel="Löschen"
        destructive
        onConfirm={remove}
      />
    </>
  );
}
